import {
  BaseReportModel,
  MessageSectionConfig,
  SectionConfig,
} from '../types/model/common';
import { IssueListReportModel } from '../types/model/issueListReportModel';
import { SingleInspectionReportModel } from '../types/model/singleInspectionReportModel';
import { SingleIssueReportModel } from '../types/model/singleIssueReportModel';
import { BaseReportOnView, IssueReportOnView } from '../types/onView/common';
import { MultiIssueReportOnView } from '../types/onView/multiIssueReportOnView';
import { SingleInspectionReportOnView } from '../types/onView/singleInspectionReportOnView';
import { SingleIssueReportOnView } from '../types/onView/singleIssueReportOnView';
import {
  issueAttachmentsConfigFromOnView,
  messageSectionConfigFromOnView,
  sectionConfigFromOnView,
} from './reportConfigOnView';
import {
  deriveInspectionDataOptionsFromForm,
  deriveInspectionReportPartsFromForm,
  deriveInspectionTemplateDataFromForm,
  deriveLinkToHustroFromForm,
} from './inspectionReportApiMapping';
import { isReportFieldsContent } from '../types/model/reportConfigGuards';

export interface IssueReportModelFields {
  fieldsContent: string;
  selectedFields: string[];
  linkedDocumentation: SectionConfig;
  events: MessageSectionConfig;
  incomingEmailMessages: MessageSectionConfig;
  workflowDecisions: MessageSectionConfig;
}

export function baseReportOnViewToModel<T extends BaseReportOnView>(
  values: T
): BaseReportModel {
  return {
    recipients: values.recipients.map((recipient) => recipient.email),
    name: values.name,
    message: values.message,
    messagePresentation: values.messagePresentation,
    localeCode: values.localeCode._id,
    issueAttachments: issueAttachmentsConfigFromOnView(values.issueAttachments),
    showFileNames: values.showFileNames,
    showFileDescriptions: values.showFileDescriptions,
    showActiveLinks: values.showActiveLinks,
    showPhoneNumbers: values.showPhoneNumbers,
    showUserEmails: values.showUserEmails,
  };
}

export function issueReportOnViewToModel<T extends IssueReportOnView>(
  values: T
): BaseReportModel & IssueReportModelFields {
  const base = baseReportOnViewToModel(values);
  return {
    ...base,
    fieldsContent: values.fieldsContent._id,
    selectedFields: values.selectedFields.map((field) => field._id),
    linkedDocumentation: sectionConfigFromOnView(values.linkedDocumentation),
    events: messageSectionConfigFromOnView(values.events),
    incomingEmailMessages: messageSectionConfigFromOnView(values.incomingEmailMessages),
    workflowDecisions: messageSectionConfigFromOnView(values.workflowDecisions),
  };
}

export function multiIssueReportOnViewToModel(
  values: MultiIssueReportOnView
): IssueListReportModel {
  return {
    ...issueReportOnViewToModel(values),
    issues: values.issues,
  };
}

export function singleInspectionReportOnViewToModel(
  values: SingleInspectionReportOnView
): SingleInspectionReportModel {
  const fieldsContentId = values.fieldsContent._id;
  if (!isReportFieldsContent(fieldsContentId)) {
    throw new Error(`Invalid inspection report fieldsContent: ${fieldsContentId}`);
  }

  return {
    ...baseReportOnViewToModel(values),
    inspection: values.inspection,
    parts: deriveInspectionReportPartsFromForm(values),
    templateData: deriveInspectionTemplateDataFromForm(values),
    inspectionData: deriveInspectionDataOptionsFromForm(values),
    fieldsContent: fieldsContentId,
    selectedFields: values.selectedFields.map((field) => field._id),
    linkToHustro: deriveLinkToHustroFromForm(values),
  };
}

export function singleIssueReportOnViewToModel(
  values: SingleIssueReportOnView
): SingleIssueReportModel {
  return {
    ...issueReportOnViewToModel(values),
    issue: values.issueId,
  };
}
